import type { DerivedDC } from '@/api/distributionCenters'
import { isBalanced, type SkuWaveLine, type SkuWaveRow } from './sku-model'

const unitsFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
})
export const dcUnits = (value: number) =>
  unitsFormatter.format(Math.abs(value) < 1e-7 ? 0 : value)
export const dcDelta = (value: number) =>
  `${value > 1e-7 ? '+' : ''}${dcUnits(value)}`

export type DcSplit = Record<string, number>
export type DcDrafts = Record<string, Record<number, DcSplit>>
export interface DcLogistics {
  multiple: number
  moq: number
}

// The workbook supplies a DC count, not store-model or destination IDs.
// Production destinations must use fetchLineDistributionCenters, never the full catalog.
export function demoDistributionCenters(count: number): DerivedDC[] {
  return Array.from({ length: count }, (_, index) => ({
    id: `demo-dc-${index + 1}`,
    code: `DC ${String(index + 1).padStart(2, '0')}`,
    name: `Demo distribution center ${index + 1}`,
  }))
}

/** Integer hundredths ensure that an equal split sums to exactly 100%. */
export function equalDcSplit(centers: DerivedDC[]): DcSplit {
  return Object.fromEntries(
    centers.map((dc, index) => [
      dc.id,
      (Math.floor(10000 / centers.length) +
        (index < 10000 % centers.length ? 1 : 0)) /
        100,
    ])
  )
}

export function skuDcLogistics(line: SkuWaveLine): DcLogistics {
  return {
    multiple: line.cp > 0 ? line.cp : 1,
    moq: line.moq,
  }
}

function validDcLogistics(logistics: DcLogistics) {
  return (
    Number.isSafeInteger(logistics.multiple) &&
    logistics.multiple > 0 &&
    Number.isSafeInteger(logistics.moq) &&
    logistics.moq >= 0
  )
}

/** Each destination rounds its share of the MOQ-adjusted wave up to a case pack. */
export function calculateDcAllocation(
  distributionQty: number,
  percent: number,
  multiple: number
) {
  const raw = (distributionQty * percent) / 100
  if (!Number.isSafeInteger(multiple) || multiple <= 0)
    throw new Error('Invalid SKU case pack')
  if (raw <= 0)
    return {
      percent,
      raw: 0,
      adjusted: 0,
      delta: 0,
    }
  const packs = raw / multiple
  // Avoid an extra case caused solely by floating-point error at a whole multiple.
  const whole = Math.round(packs)
  const stablePacks = Math.abs(packs - whole) < 1e-9 ? whole : packs
  const adjusted = Math.ceil(stablePacks) * multiple
  return {
    percent,
    raw,
    adjusted,
    delta: adjusted - raw,
  }
}

export function calculateDcRow(
  row: SkuWaveRow,
  week: number,
  centers: DerivedDC[],
  split: DcSplit
) {
  const logistics = skuDcLogistics(row.line!)
  if (!validDcLogistics(logistics))
    throw new Error('Invalid SKU logistics data')
  const waveQty = row.waves[week]?.qty ?? 0
  // Preserve the source plan; MOQ is applied once before splitting this SKU/wave.
  const distributionQty = waveQty > 0 ? Math.max(waveQty, logistics.moq) : 0
  const moqDelta = distributionQty - waveQty
  const allocations = Object.fromEntries(
    centers.map(dc => [
      dc.id,
      calculateDcAllocation(
        distributionQty,
        split[dc.id] ?? 0,
        logistics.multiple
      ),
    ])
  )
  const cells = Object.values(allocations)
  const percent = cells.reduce((sum, cell) => sum + cell.percent, 0)
  const raw = cells.reduce((sum, cell) => sum + cell.raw, 0)
  const adjusted = cells.reduce((sum, cell) => sum + cell.adjusted, 0)
  return {
    id: row.id,
    line: row.line!,
    week,
    waveQty,
    distributionQty,
    moqDelta,
    logistics,
    allocations,
    percent,
    raw,
    adjusted,
    delta: adjusted - raw,
    balance: adjusted - waveQty,
    balanced: isBalanced(percent / 100),
    needsReview: !isBalanced(percent / 100) || row.needsReview,
    sourceNeedsReview: row.needsReview,
  }
}

export type DcRow = ReturnType<typeof calculateDcRow>

/** Keep the detail workbook convention: Parent quantities are excluded from unit totals. */
export function aggregateDcRows(rows: DcRow[], centers: DerivedDC[]) {
  const unitRows = rows.filter(row => row.line.type !== 'Parent')
  const waveQty = unitRows.reduce((sum, row) => sum + row.waveQty, 0)
  const raw = unitRows.reduce((sum, row) => sum + row.raw, 0)
  const adjusted = unitRows.reduce((sum, row) => sum + row.adjusted, 0)
  // Do not let an underallocated SKU hide another SKU's excess units.
  const excessRows = unitRows.filter(row => row.balance > 1e-7)
  const excess = excessRows.reduce((sum, row) => sum + row.balance, 0)
  return {
    waveQty,
    raw,
    adjusted,
    delta: adjusted - raw,
    balance: adjusted - waveQty,
    excess,
    excessCount: excessRows.length,
    excessPercent: waveQty > 0 ? (excess / waveQty) * 100 : 0,
    centers: centers.map(dc => ({
      ...dc,
      raw: unitRows.reduce((sum, row) => sum + row.allocations[dc.id].raw, 0),
      adjusted: unitRows.reduce(
        (sum, row) => sum + row.allocations[dc.id].adjusted,
        0
      ),
    })),
  }
}
